import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ContentControlStudioLiveViewModelV2 } from '../content-control-studio-live-view-model-v2';
import { ContentControlStudioLiveTimelineComponentV2 } from '../content-control-studio-timeline/content-control-studio-live-timeline-v2.component';
import { ETimelineMode } from 'app/js/src/domain/content-control/studio/e-timeline-mode';

interface HlsProviderSnapshot {
  // Time & playback
  currentTimeSecs: number;
  rawCurrentTimeSecs: number;
  durationSecs: number;
  playingDateIso: string;
  paused: boolean;
  muted: boolean;
  playbackRate: number;
  readyState: number;
  // Frame rate
  videoFps: number;
  oneFrameSecs: number;
  // Manifest & buffer
  isManifestParsed: boolean;
  loadTimeMs: number;
  maxBufferLengthSecs: number;
  segmentDurationSecs: number;
  segmentsCount: number;
  levelTargetDuration: number;
  levelFragmentCount: number;
  levelTotalDuration: number;
  // Fragments
  hasFragments: boolean;
  hasLevelDetails: boolean;
  activeFragJson: string;
  firstFragmentJson: string;
  lastFragmentJson: string;
  // Live
  liveSyncPosition: number;
  // Dimensions
  widthPx: number;
  heightPx: number;
  // Tracks
  audioTrackNames: string;
  subtitleTrackNames: string;
}

interface LiveTimelineSnapshot {
  timelineBlockDurationSecs: number;
  isVideoTimelineFit: boolean;
  isFitSegment: boolean;
  isLiveZoomMode: boolean;
  selectedSegmentId: number | undefined;
  timelineMode: ETimelineMode;
  oneFrameSecs: number;
  videoFps: number;
  pixelsPerSecond: number;
  currentVideoTime: number;
  currentVideoPlayTimeSecs: number;
  segmentsCount: number;
}

interface LiveViewModelSnapshot {
  showDebugPanel: boolean;
  setDelayMs: number;
  allowedDeviationMs: number;
  hlsStreamDurationMs: number;
  videoWithoutCutDirectivesWithInsertsMs: number;
  overUnderRunMs: number;
  currentVideoTimeUtcMs: number;
  segmentsCount: number;
  isAudioMuted: boolean;
  isAudioForcedMuted: boolean;
  selectedAudioOption: string;
  timelineMode: ETimelineMode;
  isFitSegment: boolean;
  isLiveZoomMode: boolean;
  // State (from base)
  isFragmentDialogOpen: boolean;
  isEffectAreaDragging: boolean;
  isEffectAreaResizing: boolean;
  isVideoAudioTrack: boolean;
  isVideoPaused: boolean;
  isVideoTimelineFit: boolean;
  selectedSegmentId: number | undefined;
  selectedTimelineZoomStepIndex: number;
  selectedTimelineBlockDuration: number;
  // Zoom
  availableTimelineZoomStepsJson: string;
  timelineZoomStepsJson: string;
  // Audio & Subtitles
  audioOptionsJson: string;
  subtitleOptionsJson: string;
  selectedSubtitleOptionsJson: string;
  availableAudioTracksKeys: string;
  // Events
  multiEventsSelectedIdsJson: string;
  activeEventsCount: number;
  activeEventsJson: string;
  keyframeChangeHolderJson: string;
}

@Component({
  selector: 'evrideo-hls-debug-panel',
  templateUrl: './content-control-studio-hls-debug-panel.component.html',
  styleUrls: ['./content-control-studio-hls-debug-panel.component.scss'],
})
export class ContentControlStudioHlsDebugPanelComponent implements OnInit, OnDestroy {
  @Input() liveTimeline: ContentControlStudioLiveTimelineComponentV2;
  @Input() liveVM: ContentControlStudioLiveViewModelV2;
  @Output() closed = new EventEmitter<void>();

  hlsSnapshot: HlsProviderSnapshot;
  timelineSnapshot: LiveTimelineSnapshot;
  vmSnapshot: LiveViewModelSnapshot;
  expandedFields: Record<string, boolean> = {};
  collapsedSections: Record<string, boolean> = { hls: true, timeline: true, vm: true };
  posX = 20;
  posY = 80;

  public readonly tooltips: Record<string, string> = {
    // HLS Snapshot — Time & Playback
    currentTimeSecs: 'Current playback position as a UTC timestamp in seconds. Prefers hls.playingDate; falls back to extrapolating from the first fragment\'s programDateTime.',
    rawCurrentTimeSecs: 'Raw player-relative current time in seconds (not UTC-adjusted).',
    durationSecs: 'Total duration of the media in seconds (may be Infinity for live streams).',
    playingDate: 'The date corresponding to the current playback position.',
    paused: 'Whether the video element is currently paused.',
    muted: 'Whether the video element is muted.',
    playbackRate: 'Current playback speed multiplier (e.g. 1.0 = normal, 2.0 = double).',
    readyState: 'HTMLMediaElement ready state (0–4), indicating how much data is available.',
    // HLS Snapshot — Frame Rate
    videoFps: 'Frame rate of the video stream (frames per second).',
    oneFrameSecs: 'Duration of a single frame in seconds, derived from videoFps.',
    // HLS Snapshot — Manifest & Buffer
    isManifestParsed: 'Whether the HLS manifest has been parsed and stream metadata is available.',
    loadTimeMs: 'Timestamp (ms since epoch) when the manifest was parsed.',
    maxBufferLengthSecs: 'Maximum buffer length from HLS.js config (seconds).',
    segmentDurationSecs: 'Target segment duration from the manifest (seconds).',
    segmentsCount: 'Number of fragments in the playlist after manifest parse.',
    levelTargetDuration: 'Target segment duration from the first level (seconds).',
    levelFragmentCount: 'Number of fragments in the first level\'s playlist.',
    levelTotalDuration: 'Total duration of all fragments in the first level (seconds).',
    // HLS Snapshot — Fragments
    hasFragments: 'Whether the HLS playlist has loaded at least one fragment.',
    hasLevelDetails: 'Whether the first HLS level has parsed playlist details.',
    activeFrag: 'The currently active HLS fragment, or null before any fragment loads.',
    firstFragment: 'First fragment in the HLS playlist, or null if not yet loaded.',
    lastFragment: 'Last fragment in the HLS playlist, or null if not yet loaded.',
    // HLS Snapshot — Live
    liveSyncPosition: 'Live sync position in player seconds, or undefined if unavailable.',
    // HLS Snapshot — Dimensions
    widthPx: 'Width of the video element in pixels.',
    heightPx: 'Height of the video element in pixels.',
    // HLS Snapshot — Tracks
    audioTrackNames: 'Audio track names from the HLS manifest.',
    subtitleTrackNames: 'Subtitle track names from the HLS manifest.',
    // Live Timeline
    timelineBlockDurationSecs: 'Duration of each ruler block on the timeline in seconds.',
    isVideoTimelineFit: 'Whether the timeline is zoomed to fit the entire video duration.',
    isFitSegment: 'Whether the timeline is zoomed to fit the selected segment.',
    isLiveZoomMode: 'Whether live zoom mode is active.',
    selectedSegmentId: 'ID of the currently selected editing directive segment.',
    timelineMode: 'Current timeline display mode (OriginalTime or EditedTime).',
    pixelsPerSecond: 'Current pixel density — pixels per second of video time on the timeline.',
    currentVideoTime: 'Visible timeline duration in seconds (may exclude cut segments in ET mode).',
    currentVideoPlayTimeSecs: 'Current playback position in seconds from the video source.',
    tlSegmentsCount: 'Number of editing directive segments in the view model.',
    // Live VM — Delay & Timing
    setDelayMs: 'User-defined delay for the studio in milliseconds.',
    allowedDeviationMs: 'User-defined allowed deviation for the studio in milliseconds.',
    hlsStreamDurationMs: '[Max Delay] Total HLS buffer/window duration in milliseconds.',
    videoWithoutCutDirectivesWithInsertsMs: '[Current Delay] Total delay minus Cut durations plus Insert durations, clamped to >= 0.',
    overUnderRunMs: 'Over/under-run in ms: allowedDeviation + setDelay - currentDelay.',
    currentVideoTimeUtcMs: 'Computed UTC timestamp of the current video position in milliseconds.',
    // Live VM — State
    showDebugPanel: 'Whether the debug panel overlay is visible.',
    vmSegmentsCount: 'Number of editing directive segments.',
    isAudioMuted: 'Whether the audio is currently muted by user toggle.',
    isAudioForcedMuted: 'Whether the audio is force-muted by the system (e.g. during seek).',
    selectedAudioOption: 'Currently selected audio track option name.',
    isFragmentDialogOpen: 'Whether the fragment/segment editing dialog is open.',
    isEffectAreaDragging: 'Whether an effect area (graphic overlay) is being dragged on the canvas.',
    isEffectAreaResizing: 'Whether an effect area (graphic overlay) is being resized on the canvas.',
    isVideoAudioTrack: 'Whether the SOURCE (video\'s own audio) track is selected.',
    isVideoPaused: 'Whether video playback is currently paused.',
    selectedTimelineZoomStepIndex: 'Index of the currently selected zoom step in the available steps array.',
    selectedTimelineBlockDuration: 'Duration of a single timeline ruler block at the current zoom level (seconds).',
    // Live VM — Zoom
    availableTimelineZoomSteps: 'Available zoom step durations (seconds) calculated from video duration.',
    timelineZoomSteps: 'Zoom levels as ruler block durations (seconds).',
    // Live VM — Audio & Subtitles
    audioOptions: 'List of available audio track option names.',
    subtitleOptions: 'List of available subtitle track option names.',
    selectedSubtitleOptions: 'List of currently selected subtitle track option names.',
    availableAudioTracksKeys: 'Keys of the available audio tracks dictionary.',
    // Live VM — Events
    multiEventsSelectedIds: 'IDs of segments currently selected in multi-select mode.',
    activeEventsCount: 'Number of active editing directive events at the playhead position.',
    activeEvents: 'Full details of active editing directive events at the playhead position.',
    keyframeChangeHolder: 'The keyframe currently being modified by effect area interaction.',
  };

  private _interval: ReturnType<typeof setInterval>;
  private _dragging = false;
  private _dragOffsetX = 0;
  private _dragOffsetY = 0;

  ngOnInit(): void {
    this._updateSnapshot();
    this._interval = setInterval(() => this._updateSnapshot(), 250);
  }

  ngOnDestroy(): void {
    if (this._interval) {
      clearInterval(this._interval);
    }
  }

  onHeaderMouseDown(event: MouseEvent): void {
    this._dragging = true;
    this._dragOffsetX = event.clientX - this.posX;
    this._dragOffsetY = event.clientY - this.posY;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this._dragging) return;
    this.posX = event.clientX - this._dragOffsetX;
    this.posY = event.clientY - this._dragOffsetY;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this._dragging = false;
  }

  toggleField(field: string): void {
    this.expandedFields[field] = !this.expandedFields[field];
  }

  toggleSection(section: string): void {
    this.collapsedSections[section] = !this.collapsedSections[section];
  }

  formatTimestamp(ms: number): string {
    if (!ms || ms <= 0) return 'N/A';
    try {
      return new Date(ms).toISOString();
    } catch {
      return 'Invalid';
    }
  }

  formatSecsTimecode(secs: number): string {
    if (secs == null || !Number.isFinite(secs)) return 'N/A';
    const sign = secs < 0 ? '-' : '';
    const abs = Math.abs(secs);
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const s = Math.floor(abs % 60);
    const ms = Math.round((abs % 1) * 1000);
    return `${sign}${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }

  formatMsTimecode(ms: number): string {
    if (ms == null || !Number.isFinite(ms)) return 'N/A';
    return this.formatSecsTimecode(ms / 1000);
  }

  private _updateSnapshot(): void {
    this._updateHlsSnapshot();
    this._updateTimelineSnapshot();
    this._updateVmSnapshot();
  }

  private _updateHlsSnapshot(): void {
    if (!this.liveVM) return;
    const hs = this.liveVM.hlsVideoSource;
    this.hlsSnapshot = {
      // Time & playback
      currentTimeSecs: hs.currentTimeSecs,
      rawCurrentTimeSecs: hs.rawCurrentTimeSecs,
      durationSecs: hs.durationSecs,
      playingDateIso: hs.playingDate?.toISOString() ?? 'N/A',
      paused: hs.paused,
      muted: hs.muted,
      playbackRate: hs.playbackRate,
      readyState: hs.readyState,
      // Frame rate
      videoFps: hs.videoFps,
      oneFrameSecs: hs.oneFrameSecs,
      // Manifest & buffer
      isManifestParsed: hs.isManifestParsed,
      loadTimeMs: hs.loadTimeMs,
      maxBufferLengthSecs: hs.maxBufferLengthSecs,
      segmentDurationSecs: hs.segmentDurationSecs,
      segmentsCount: hs.segmentsCount,
      levelTargetDuration: hs.levelTargetDuration,
      levelFragmentCount: hs.levelFragmentCount,
      levelTotalDuration: hs.levelTotalDuration,
      // Fragments
      hasFragments: hs.hasFragments,
      hasLevelDetails: hs.hasLevelDetails,
      activeFragJson: this._safeStringify(hs.activeFrag),
      firstFragmentJson: this._safeStringify(hs.firstFragment),
      lastFragmentJson: this._safeStringify(hs.lastFragment),
      // Live
      liveSyncPosition: hs.liveSyncPosition ?? 0,
      // Dimensions
      widthPx: hs.widthPx,
      heightPx: hs.heightPx,
      // Tracks
      audioTrackNames: hs.audioTrackNames.join(', ') || 'none',
      subtitleTrackNames: hs.subtitleTrackNames.join(', ') || 'none',
    };
  }

  private _updateTimelineSnapshot(): void {
    if (!this.liveTimeline) return;
    const t = this.liveTimeline;
    
    this.timelineSnapshot = {
      timelineBlockDurationSecs: t.timelineBlockDurationSecs,
      isVideoTimelineFit: t.isVideoTimelineFit,
      isFitSegment: t.isFitSegment,
      isLiveZoomMode: t.vm.isLiveZoomMode,
      selectedSegmentId: t.selectedSegmentId,
      timelineMode: t.vm.timelineMode,
      oneFrameSecs: t.oneFrameSecs,
      videoFps: t.videoFps,
      pixelsPerSecond: t.pixelsPerSecond(),
      currentVideoTime: t.currentVideoTimeSecs(t.isFitSegment, t.isEditedTimeline),
      currentVideoPlayTimeSecs: t.currentVideoPlayTimeSecs(),
      segmentsCount: t.vm.segments?.length ?? 0,
    };
  }

  private _updateVmSnapshot(): void {
    if (!this.liveVM) return;
    const vm = this.liveVM;
    this.vmSnapshot = {
      showDebugPanel: vm.showDebugPanel,
      setDelayMs: vm.setDelayMs,
      allowedDeviationMs: vm.allowedDeviationMs,
      hlsStreamDurationMs: vm.hlsVideoSource.maxBufferLengthSecs * 1000,
      videoWithoutCutDirectivesWithInsertsMs: vm.videoWithoutCutDirectivesWithInsertsMs,
      overUnderRunMs: vm.overUnderRunMs,
      currentVideoTimeUtcMs: vm.hlsVideoSource.currentVideoTimeUtcMs,
      segmentsCount: vm.segments?.length ?? 0,
      isAudioMuted: vm.isAudioMuted,
      isAudioForcedMuted: vm.isAudioForcedMuted,
      selectedAudioOption: vm.selectedAudioOption,
      timelineMode: vm.timelineMode,
      isFitSegment: vm.isFitSegment,
      isLiveZoomMode: vm.isLiveZoomMode,
      // State (from base)
      isFragmentDialogOpen: vm.isFragmentDialogOpen,
      isEffectAreaDragging: vm.isEffectAreaDragging,
      isEffectAreaResizing: vm.isEffectAreaResizing,
      isVideoAudioTrack: vm.isVideoAudioTrack,
      isVideoPaused: vm.isVideoPaused,
      isVideoTimelineFit: vm.isVideoTimelineFit,
      selectedSegmentId: vm.selectedSegmentId,
      selectedTimelineZoomStepIndex: vm.selectedTimelineZoomStepIndex,
      selectedTimelineBlockDuration: vm.selectedTimelineBlockDuration,
      // Zoom
      availableTimelineZoomStepsJson: JSON.stringify(vm.availableTimelineZoomSteps ?? []),
      timelineZoomStepsJson: JSON.stringify(vm.timelineZoomSteps ?? []),
      // Audio & Subtitles
      audioOptionsJson: JSON.stringify(vm.audioOptions ?? []),
      subtitleOptionsJson: JSON.stringify(vm.subtitleOptions ?? []),
      selectedSubtitleOptionsJson: JSON.stringify(vm.selectedSubtitleOptions ?? []),
      availableAudioTracksKeys: Object.keys(vm.availableAudioTracks ?? {}).join(', ') || 'none',
      // Events
      multiEventsSelectedIdsJson: JSON.stringify(vm.multiEventsSelectedIds ?? []),
      activeEventsCount: vm.activeEvents?.length ?? 0,
      activeEventsJson: this._safeStringify(vm.activeEvents),
      keyframeChangeHolderJson: this._safeStringify(vm.keyframeChangeHolder),
    };
  }

  private _safeStringify(obj: unknown): string {
    if (obj == null) return 'null';
    try {
      const seen = new WeakSet();
      return JSON.stringify(obj, (_key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);
        }
        if (value instanceof HTMLElement) return '[HTMLElement]';
        if (value instanceof ArrayBuffer) return '[ArrayBuffer]';
        if (value instanceof Uint8Array) return `[Uint8Array(${value.length})]`;
        return value;
      }, 2);
    } catch {
      return '[Unstringifiable]';
    }
  }
}
